import { Box, Button, Chip, IconButton, Tooltip, Typography } from "@mui/material";
import {
  Mic,
  MicOff,
  Videocam,
  VideocamOff,
  CallEnd,
  VideoCall,
  Security,
} from "@mui/icons-material";
import { useTranslation } from "react-i18next";
import { appColors, primaryAlpha, radius } from "../../constants/theme";

const controlButtonSx = ({ active, danger = false, mutedLook = false }) => ({
  width: 52,
  height: 52,
  borderRadius: `${radius.lg}px`,
  border: `1px solid ${
    danger
      ? primaryAlpha(0)
      : active
        ? primaryAlpha(0.35)
        : appColors.gray200
  }`,
  bgcolor: danger
    ? appColors.error
    : active
      ? appColors.primaryBg
      : mutedLook
        ? appColors.gray50
        : appColors.white,
  color: danger
    ? appColors.white
    : active
      ? appColors.primaryDeep
      : appColors.gray900,
  boxShadow: danger
    ? `0 6px 16px ${primaryAlpha(0.12)}`
    : active
      ? `0 4px 12px ${primaryAlpha(0.18)}`
      : "0 2px 8px rgba(15, 23, 42, 0.06)",
  transition: "background-color 0.15s ease, border-color 0.15s ease, box-shadow 0.15s ease",
  "&:hover": {
    bgcolor: danger
      ? appColors.errorDark
      : active
        ? appColors.primaryBgHover
        : appColors.bgMuted,
    borderColor: danger ? appColors.errorDark : appColors.primary,
  },
  "&.Mui-disabled": {
    bgcolor: appColors.gray50,
    color: appColors.gray500,
    borderColor: appColors.gray200,
    boxShadow: "none",
  },
});

/**
 * Lobide: mikrofon / kamera / Görüşmeye Katıl
 * Görüşmede: mikrofon / kamera / görüşmeyi bitir
 */
const MeetingMediaControls = ({
  inCall,
  audioMuted,
  videoMuted,
  disabled,
  onToggleAudio,
  onToggleVideo,
  onJoin,
  onLeave,
}) => {
  const { t } = useTranslation();

  return (
    <Box
      sx={{
        flexShrink: 0,
        width: "100%",
        display: "flex",
        justifyContent: "center",
        alignItems: "center",
        gap: { xs: 1.25, sm: 1.75 },
        py: 1.5,
        px: 2,
        bgcolor: appColors.white,
        borderTop: `1px solid ${appColors.gray200}`,
        background: `linear-gradient(180deg, ${appColors.white} 0%, ${appColors.tealPale} 140%)`,
        zIndex: 5,
      }}
    >
      <Tooltip
        title={
          audioMuted
            ? t("meeting_control_unmute_mic", { defaultValue: "Mikrofonu aç" })
            : t("meeting_control_mute_mic", { defaultValue: "Mikrofonu kapat" })
        }
      >
        <span>
          <IconButton
            disabled={disabled}
            onClick={onToggleAudio}
            aria-label={
              audioMuted
                ? t("meeting_control_unmute_mic", { defaultValue: "Mikrofonu aç" })
                : t("meeting_control_mute_mic", { defaultValue: "Mikrofonu kapat" })
            }
            sx={controlButtonSx({ active: !audioMuted, mutedLook: audioMuted })}
          >
            {audioMuted ? <MicOff /> : <Mic />}
          </IconButton>
        </span>
      </Tooltip>

      <Tooltip
        title={
          videoMuted
            ? t("open_camera", { defaultValue: "Kamerayı aç" })
            : t("close_camera", { defaultValue: "Kamerayı kapat" })
        }
      >
        <span>
          <IconButton
            disabled={disabled}
            onClick={onToggleVideo}
            aria-label={
              videoMuted
                ? t("open_camera", { defaultValue: "Kamerayı aç" })
                : t("close_camera", { defaultValue: "Kamerayı kapat" })
            }
            sx={controlButtonSx({ active: !videoMuted, mutedLook: videoMuted })}
          >
            {videoMuted ? <VideocamOff /> : <Videocam />}
          </IconButton>
        </span>
      </Tooltip>

      {!inCall ? (
        <Button
          variant="contained"
          disabled={disabled}
          onClick={onJoin}
          startIcon={<VideoCall />}
          sx={{
            textTransform: "none",
            fontWeight: 700,
            borderRadius: `${radius.lg}px`,
            px: 2.75,
            py: 1.2,
            minHeight: 52,
            background: `linear-gradient(135deg, ${appColors.primary} 0%, ${appColors.primaryTeal} 100%)`,
            boxShadow: `0 8px 20px ${primaryAlpha(0.28)}`,
            "&:hover": {
              background: `linear-gradient(135deg, ${appColors.primaryDark} 0%, ${appColors.primary} 100%)`,
              boxShadow: `0 10px 24px ${primaryAlpha(0.34)}`,
            },
            "&.Mui-disabled": {
              bgcolor: appColors.gray50,
              color: appColors.gray500,
              boxShadow: "none",
            },
          }}
        >
          {t("join_meeting", { defaultValue: "Görüşmeye Katıl" })}
        </Button>
      ) : (
        <Tooltip title={t("end_call", { defaultValue: "Görüşmeyi bitir" })}>
          <span>
            <IconButton
              disabled={disabled}
              onClick={onLeave}
              aria-label={t("end_call", { defaultValue: "Görüşmeyi bitir" })}
              sx={controlButtonSx({ active: false, danger: true })}
            >
              <CallEnd />
            </IconButton>
          </span>
        </Tooltip>
      )}
    </Box>
  );
};

export const MeetingLobbyPreview = ({ displayName, audioMuted, videoMuted }) => {
  const { t } = useTranslation();

  return (
    <Box
      sx={{
        flex: 1,
        minHeight: 0,
        width: "100%",
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        justifyContent: "center",
        gap: 2.5,
        px: 3,
        py: 4,
        background: `
          radial-gradient(ellipse 80% 60% at 50% 0%, ${primaryAlpha(0.18)} 0%, transparent 55%),
          linear-gradient(165deg, ${appColors.tealPale} 0%, ${appColors.white} 42%, ${appColors.bgMuted} 100%)
        `,
        color: appColors.gray900,
      }}
    >
      <Box
        sx={{
          width: 96,
          height: 96,
          borderRadius: "50%",
          background: `linear-gradient(135deg, ${appColors.tealBg} 0%, ${appColors.tealBorder} 100%)`,
          border: `1px solid ${appColors.tealBorder}`,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          boxShadow: `0 12px 28px ${primaryAlpha(0.2)}`,
        }}
      >
        <VideoCall sx={{ fontSize: 44, color: appColors.primary }} />
      </Box>

      <Box sx={{ textAlign: "center", maxWidth: 420 }}>
        <Typography
          variant="overline"
          sx={{
            color: appColors.primaryDark,
            fontWeight: 700,
            letterSpacing: 1.1,
            display: "block",
            mb: 0.75,
          }}
        >
          Medilya
        </Typography>
        <Typography
          variant="h5"
          sx={{
            fontWeight: 700,
            textAlign: "center",
            color: appColors.gray900,
            fontSize: { xs: "1.25rem", sm: "1.4rem" },
            mb: 1,
          }}
        >
          {displayName || t("meeting_lobby_title", { defaultValue: "Görüşme hazır" })}
        </Typography>
        <Typography
          variant="body2"
          sx={{ color: appColors.gray500, textAlign: "center", lineHeight: 1.6 }}
        >
          {t("meeting_lobby_hint", {
            defaultValue:
              "Mikrofon ve kamera tercihlerinizi seçip Görüşmeye Katıl ile devam edin.",
          })}
        </Typography>
      </Box>

      <Box sx={{ display: "flex", flexWrap: "wrap", justifyContent: "center", gap: 1 }}>
        <Chip
          size="small"
          icon={audioMuted ? <MicOff sx={{ fontSize: 16 }} /> : <Mic sx={{ fontSize: 16 }} />}
          label={
            audioMuted
              ? t("meeting_control_mute_mic", { defaultValue: "Mikrofon kapalı" })
              : t("meeting_control_unmute_mic", { defaultValue: "Mikrofon açık" })
          }
          sx={{
            bgcolor: audioMuted ? appColors.gray50 : appColors.primaryBg,
            color: audioMuted ? appColors.gray500 : appColors.primaryDeep,
            border: `1px solid ${audioMuted ? appColors.gray200 : appColors.tealBorder}`,
            fontWeight: 600,
            "& .MuiChip-icon": {
              color: audioMuted ? appColors.gray500 : appColors.primary,
            },
          }}
        />
        <Chip
          size="small"
          icon={
            videoMuted ? (
              <VideocamOff sx={{ fontSize: 16 }} />
            ) : (
              <Videocam sx={{ fontSize: 16 }} />
            )
          }
          label={
            videoMuted
              ? t("close_camera", { defaultValue: "Kamera kapalı" })
              : t("open_camera", { defaultValue: "Kamera açık" })
          }
          sx={{
            bgcolor: videoMuted ? appColors.gray50 : appColors.primaryBg,
            color: videoMuted ? appColors.gray500 : appColors.primaryDeep,
            border: `1px solid ${videoMuted ? appColors.gray200 : appColors.tealBorder}`,
            fontWeight: 600,
            "& .MuiChip-icon": {
              color: videoMuted ? appColors.gray500 : appColors.primary,
            },
          }}
        />
      </Box>

      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          gap: 1,
          mt: 0.5,
          px: 1.75,
          py: 1,
          borderRadius: `${radius.md}px`,
          bgcolor: primaryAlpha(0.06),
          border: `1px solid ${appColors.tealBorder}`,
          maxWidth: 420,
        }}
      >
        <Security sx={{ fontSize: 18, color: appColors.primary, flexShrink: 0 }} />
        <Typography variant="caption" sx={{ color: appColors.primaryDeep, lineHeight: 1.45 }}>
          {t("meeting_lobby_secure_hint", {
            defaultValue:
              "Görüşme bağlantınız güvenli şekilde şifrelenir. İzinleri istediğiniz zaman değiştirebilirsiniz.",
          })}
        </Typography>
      </Box>
    </Box>
  );
};

export default MeetingMediaControls;
